import { useLayoutEffect } from 'react';

interface Edges {
  left: number;
  right: number;
}

export interface Bleed {
  /** Distance from the content column's start edge to the clipping ancestor's start edge. */
  start: number;
  /** Distance from the content column's end edge to the clipping ancestor's end edge. */
  end: number;
}

export interface ScrollEdges {
  /** Content is hidden before the scroll start. */
  start: boolean;
  /** Content is hidden past the scroll end. */
  end: boolean;
}

/** How far a column has to extend on each side to reach the clip edges. Never negative. */
export function computeBleed(column: Edges, clip: Edges): Bleed {
  return {
    start: Math.max(0, Math.round(column.left - clip.left)),
    end: Math.max(0, Math.round(clip.right - column.right)),
  };
}

/** Which sides of a horizontal scroller still have content out of view. */
export function computeScrollEdges(
  {
    scrollLeft,
    scrollWidth,
    clientWidth,
  }: Pick<Element, 'scrollLeft' | 'scrollWidth' | 'clientWidth'>,
  threshold = 1
): ScrollEdges {
  return {
    start: scrollLeft > threshold,
    end: scrollWidth - clientWidth - scrollLeft > threshold,
  };
}

/** Nearest ancestor that clips horizontal overflow; the viewport when none does. */
export function findClipAncestor(element: HTMLElement): HTMLElement | null {
  let node = element.parentElement;
  while (node && node !== document.documentElement) {
    const overflowX = getComputedStyle(node).overflowX;
    if (overflowX !== 'visible') return node;
    node = node.parentElement;
  }
  return null;
}

/**
 * Lets a horizontal scroller bleed out of its padded content column to the edges of the
 * nearest clipping ancestor, and tracks which sides still hide content.
 *
 * `frame` wraps the scroller and is what moves: the hook writes CSS custom properties
 * (`--bleed-start`, `--bleed-end`) and data attributes (`data-scroll-start`,
 * `data-scroll-end`) on it and nothing else. The stylesheet decides how to use them, so
 * the hook is a no-op where the styles don't apply (e.g. the stacked mobile layout).
 * Keeping the fades on the frame rather than masking the scroller leaves the scroller a
 * plain composited layer, so scrolling stays off the main thread.
 *
 * The bleed is measured from the frame's parent so that applying the negative margins
 * never changes what is being measured.
 */
export function useEdgeToEdgeScroll(
  frame: HTMLElement | null,
  scroller: HTMLElement | null,
  enabled = true
) {
  useLayoutEffect(() => {
    if (!frame || !scroller || !enabled) return;
    const column = frame.parentElement;
    if (!column) return;
    const clip = findClipAncestor(frame);

    const measureBleed = () => {
      const columnRect = column.getBoundingClientRect();
      const clipEdges: Edges = clip
        ? (() => {
            const rect = clip.getBoundingClientRect();
            const left = rect.left + clip.clientLeft;
            return { left, right: left + clip.clientWidth };
          })()
        : { left: 0, right: document.documentElement.clientWidth };
      const bleed = computeBleed(columnRect, clipEdges);
      frame.style.setProperty('--bleed-start', `${bleed.start}px`);
      frame.style.setProperty('--bleed-end', `${bleed.end}px`);
    };

    // Scroll fires every frame; only touch the DOM when a side actually changes.
    let edges: ScrollEdges | undefined;
    const measureScroll = () => {
      const next = computeScrollEdges(scroller);
      if (edges?.start !== next.start) frame.dataset.scrollStart = String(next.start);
      if (edges?.end !== next.end) frame.dataset.scrollEnd = String(next.end);
      edges = next;
    };

    const measure = () => {
      measureBleed();
      measureScroll();
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(column);
    observer.observe(scroller);
    if (clip) observer.observe(clip);
    scroller.addEventListener('scroll', measureScroll, { passive: true });
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      scroller.removeEventListener('scroll', measureScroll);
      window.removeEventListener('resize', measure);
      frame.style.removeProperty('--bleed-start');
      frame.style.removeProperty('--bleed-end');
      delete frame.dataset.scrollStart;
      delete frame.dataset.scrollEnd;
    };
  }, [frame, scroller, enabled]);
}
